import classNames from 'classnames';
import { useInstantSearch, useSearchBox } from 'react-instantsearch';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import type { ExtendedUIState } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  useGQLSearchContext,
  useClearAllFilters as useSearchClearAllFilters,
  useFiltersCount as useSearchFiltersCount,
  useToggleChip,
} from '@sorare/core/src/contexts/graphql-search';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import type { Filter } from '@sorare/core/src/lib/filters';
import { FILTERS, TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useRemoveMarketFilterChipEvent } from 'hooks/events/useRemoveMarketFilterChipEvent';
import {
  isListFilterWidget,
  isRangeFilterWidget,
  useFormatFilterWidgetValue,
} from 'hooks/search/useFormatFilterWidgetValue';
import { useRelevantRefinements } from 'hooks/search/useRelevantRefinements';
import { useClearAllFilters } from 'search/ClearAllFilters';
import { useFiltersCount } from 'search/FiltersManager/useFiltersCount';
import { GQL_SEARCH_FILTERS } from 'searchCommonPlayer/AdvancedPlayerSearch/filters';

import { FilterChip } from './FilterChip';
import { ListActiveFilter } from './ListActiveFilter';
import { RangeActiveFilter } from './RangeActiveFilter';
import { VirtualToggleActiveFilters } from './VirtualToggleActiveFilters';
import type { RefinementItem } from './types';

const Container = styled.div`
  display: flex;
  gap: var(--unit);
  flex-wrap: nowrap;
  overflow-x: auto;
  & > * {
    flex: none;
  }
  @media ${tabletAndAbove} {
    flex-wrap: wrap;
    & > * {
      flex: initial;
    }
    &.forceScroll {
      flex-wrap: nowrap;
      & > * {
        flex: none;
      }
    }
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ActiveFilter = ({ item }: { item: RefinementItem }) => {
  const { sport } = useSportContext();
  const trackRemoveMarketFilterChip = useRemoveMarketFilterChipEvent();
  const { formatFilterWidgetValue } = useFormatFilterWidgetValue();

  if (
    [
      TOGGLE_FILTERS.creditEligibleFilter.attribute,
      TOGGLE_FILTERS.onSaleFilter.attribute,
      TOGGLE_FILTERS.bidToEarnFilter.attribute,
    ].includesValue(item.attribute)
  ) {
    const [label] = formatFilterWidgetValue(item);

    return (
      <FilterChip
        label={label}
        onClose={() => {
          item.refine(item.refinements?.[0]);
          if (sport) {
            trackRemoveMarketFilterChip(item.attribute, 'false', sport);
          }
        }}
      />
    );
  }

  if (isListFilterWidget(item)) {
    return <ListActiveFilter item={item} />;
  }

  if (isRangeFilterWidget(item)) {
    return <RangeActiveFilter item={item} />;
  }

  return null;
};

export const ActiveFilters = ({ forceScroll }: { forceScroll?: boolean }) => {
  const { sport } = useSportContext();
  const { formatMessage } = useIntl();
  const trackRemoveMarketFilterChip = useRemoveMarketFilterChipEvent();
  const filtersCount = useFiltersCount({ countSealed: false });
  const items = useRelevantRefinements();
  const {
    indexUiState: { exclusions = {} },
    setIndexUiState,
  } = useInstantSearch<ExtendedUIState>();
  const { query, clear } = useSearchBox();
  const clearAllFilters = useClearAllFilters();
  const setVirtualToggleFilters = useVirtualToggleManager();

  if (!filtersCount) {
    return null;
  }

  return (
    <Container className={classNames({ forceScroll })}>
      {!!query && (
        <FilterChip
          label={
            <FormattedMessage
              id="ActiveFilters.search"
              defaultMessage="Search: {query}"
              values={{ query }}
            />
          }
          onClose={() => {
            clear();
            if (sport) {
              trackRemoveMarketFilterChip('query', query, sport);
            }
          }}
        />
      )}
      {items.map(item => (
        <ActiveFilter key={item.attribute} item={item as RefinementItem} />
      ))}
      {Object.entries(exclusions)
        .filter(([, value]) => value)
        .map(([key]) => {
          const filter = (Object.values(FILTERS) as Filter[]).find(
            f => f.alias === key
          );
          return (
            <FilterChip
              key={key}
              label={filter ? formatMessage(filter.title) : key}
              onClose={() => {
                setIndexUiState(uiState => ({
                  ...uiState,
                  exclusions: {
                    ...uiState.exclusions,
                    [key]: false,
                  },
                }));
                if (sport) {
                  trackRemoveMarketFilterChip(key, 'false', sport);
                }
              }}
            />
          );
        })}
      <VirtualToggleActiveFilters
        setVirtualToggleFilters={setVirtualToggleFilters}
      />
      <Button size="small" onClick={clearAllFilters} color="red">
        <FormattedMessage {...filters.clearAll} />
      </Button>
    </Container>
  );
};

export const BackendActiveFilters = ({
  forceScroll,
}: {
  forceScroll?: boolean;
}) => {
  const {
    query,
    activeFacets,
    facets,
    setSearchParams,
    getRefinementType,
    getRefinementApiAttribute,
  } = useGQLSearchContext()!;
  const { sport } = useSportContext();
  const trackRemoveMarketFilterChip = useRemoveMarketFilterChipEvent();
  const toggleChip = useToggleChip();

  const filtersCount = useSearchFiltersCount();

  const clear = () => {
    setSearchParams({ q: [] });
  };
  const clearAllFilters = useSearchClearAllFilters();
  const items = Object.entries(activeFacets).filter(e => e[1].length);

  if (!filtersCount) {
    return null;
  }

  return (
    <Container className={classNames({ forceScroll })}>
      {!!query && (
        <FilterChip
          label={
            <FormattedMessage
              id="ActiveFilters.search"
              defaultMessage="Search: {query}"
              values={{ query }}
            />
          }
          onClose={() => {
            clear();
            if (sport) {
              trackRemoveMarketFilterChip('query', query, sport);
            }
          }}
        />
      )}
      {items.map(([urlAttribute, values]) => {
        const refinementType = getRefinementType(urlAttribute);
        if (refinementType === 'virtual') {
          if (urlAttribute === GQL_SEARCH_FILTERS.leagueFilter) {
            const getLabel = (value: string) => {
              return facets[GQL_SEARCH_FILTERS.leagueFilter].values.find(
                e => e.value === value
              )?.label;
            };
            return (
              <ActiveFilter
                key={urlAttribute}
                item={
                  {
                    attribute: getRefinementApiAttribute(urlAttribute),
                    refine: ({ value }) => {
                      toggleChip({ value, attribute: urlAttribute });
                    },
                    refinements: values.map(value => ({
                      value,
                      label: getLabel(value),
                      type: 'disjunctive',
                    })),
                  } as RefinementItem<string>
                }
              />
            );
          }
          return null;
        }
        return (
          <ActiveFilter
            key={urlAttribute}
            item={
              {
                attribute: getRefinementApiAttribute(urlAttribute),
                refine: ({ value }) => {
                  toggleChip({ value, attribute: urlAttribute });
                },
                refinements:
                  refinementType === 'string'
                    ? values.map(value => ({
                        value,
                        label: value,
                        type: 'disjunctive',
                      }))
                    : values
                        .map((value, i) => ({
                          value,
                          label: value,
                          operator: i === 0 ? '>=' : '<=',
                          type: 'numeric',
                        }))
                        .filter(e => e.value),
              } as RefinementItem<string>
            }
          />
        );
      })}
      <Button size="small" onClick={clearAllFilters} color="red">
        <FormattedMessage {...filters.clearAll} />
      </Button>
    </Container>
  );
};
